import { IRenderingCommonContext } from "@commonRendering/types";
import { WidgetRendererBase } from "../widget-renderer-base";
import { ICalendarEventListWidgetData, ICalendarEventListWidgetStyle, } from "@pageBuilder/typings";
import { CalendarEventListWidgetScriptRenderer } from "./calendar-event-list-widget-script.renderer";
import { CssProperties } from "../../../models/css-property";
import { StringUtil } from "../../../utils/string-util";

export class CalendarEventListWidgetRenderer extends WidgetRendererBase<ICalendarEventListWidgetData> {

    private readonly scriptRenderer: CalendarEventListWidgetScriptRenderer;

    constructor(renderingCommonContext: IRenderingCommonContext) {
        super(renderingCommonContext);

        this.scriptRenderer = new CalendarEventListWidgetScriptRenderer();
    }

    protected createNewInternal(): HTMLElement {
        return this.createWidgetElement("calendarEventListWidget");
    }

    protected renderInternal(element: HTMLElement, data: ICalendarEventListWidgetData): HTMLElement {
        const existingData = this.getData(element);
        element.setAttribute("data-layout", data.layout);
        element.toggleAttribute("data-primary-element", data.primaryElement);

        this.renderStyles(element, data);

        this.scriptRenderer.renderCommon(this.renderedDocument);
        if (existingData?.layout !== data.layout) {
            this.scriptRenderer.renderLocal(element);
        }

        return element;
    }

    protected renderStyles(element: HTMLElement, data: ICalendarEventListWidgetData): void {
        const widgetContent = element.querySelector<HTMLElement>(".widget-content");
        widgetContent?.classList.toggle("secondary-button", data.style?.button?.theme === "secondary");

        this.setContentStyles(element, data.style);
        this.setEventInfoStyles(element, data.style);
        this.setButtonStyles(element, data.style);

        this.animationRenderer.setAnimationStyles(element, data.style.animation);
    }

    private setContentStyles(element: HTMLElement, style: ICalendarEventListWidgetStyle): void {
        const cssProperties = new CssProperties();

        cssProperties.tryAdd("--widget-background-color", style.backgroundColor ?? "");

        this.styleService.setStyle(element, `#${element.id} .widget-content`, cssProperties.data);
    }

    private setEventInfoStyles(element: HTMLElement, style: ICalendarEventListWidgetStyle): void {
        const cssProperties = new CssProperties();

        cssProperties.tryAdd("--heading-font-family", style.headingFont.family ?? "");
        cssProperties.tryAdd("--heading-font-weight", style.headingFont.weight ?? "");
        cssProperties.tryAdd("--heading-font-color", style.headingFont.color ?? "");
        cssProperties.tryAdd("--date-font-color", style.dateFont.color ?? "");
        cssProperties.tryAdd("--price-font-color", style.priceFont.color ?? "");

        this.styleService.setStyle(element, `#${element.id} .event-info`, cssProperties.data);
    }

    private setButtonStyles(element: HTMLElement, style: ICalendarEventListWidgetStyle): void {
        const cssProperties = new CssProperties();

        if (style.button.font?.size) {
            cssProperties.tryAdd("--font-size", String(StringUtil.getNumberFromStr(style.button.font.size)));
        }
        cssProperties.tryAdd("--font-family", style.button.font?.family ?? "");
        cssProperties.tryAdd("--font-height", style.button.font?.lineHeight ?? "");
        cssProperties.tryAdd("--font-weight", style.button.font?.weight ?? "");
        cssProperties.tryAdd("--color", style.button.color ?? "");
        cssProperties.tryAdd("--hovered-color", style.button.hoverColor ?? "");
        cssProperties.tryAdd("--border-color", style.button.borderColor ?? "");
        cssProperties.tryAdd("--hovered-border-color", style.button.borderHoverColor ?? "");

        cssProperties.tryAdd("border-width", style.button.thickness);
        if (style.button.borderRadius) {
            cssProperties.tryAdd("border-top-left-radius", `${style.button.borderRadius.topLeftPx}px`);
            cssProperties.tryAdd("border-top-right-radius", `${style.button.borderRadius.topRightPx}px`);
            cssProperties.tryAdd("border-bottom-left-radius", `${style.button.borderRadius.bottomLeftPx}px`);
            cssProperties.tryAdd("border-bottom-right-radius", `${style.button.borderRadius.bottomRightPx}px`);
        }

        cssProperties.tryAdd("--background-color", style.button.backgroundColor ?? "");
        cssProperties.tryAdd("--hovered-background-color", style.button.backgroundHoverColor ?? "");

        this.styleService.setStyle(element, `#${element.id} .button-element`, cssProperties.data);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    protected cleanupInternal(element: HTMLElement): void {
        // nothing
    }

    protected getContentSelectorsInternal(element: HTMLElement): Array<string> {
        return [
            `#${element.id}`,
            `#${element.id} .widget-content`,
            `#${element.id} .event-info`,
            `#${element.id} .button-element`
        ];
    }

}
